<template>
  <component :is="tooltipOrDiv" v-bind="tooltipProps">
    <div :class="parentHeight" class="bitts-avatar-pip">
      <BittsAvatar
        v-bind="userOrOrg(true)"
        :alt-text-from-parent="altTextFromParent"
        :image-source="imageSource"
        :show-initials="showInitials"
        :size="size"
      />
      <BittsAvatar
        v-if="!isIcon"
        v-bind="userOrOrg(false)"
        :size="pipSize"
        :class="pipContainerSize"
        :avatar-classes="pipOutlineClass"
        :show-initials="true"
        :shape="!isUserMainAvatar ? 'circle' : 'square'"
        class="bitts-avatar-pip__pip"
      />
      <BittsAvatar
        v-else
        :is-icon="true"
        :size="pipSize"
        :avatar-classes="pipOutlineClass"
        :icon-slot-bg-color="iconSlotBgColor"
        shape="circle"
        class="bitts-avatar-pip__pip"
      >
        <template #icon>
          <slot name="icon" />
        </template>
      </BittsAvatar>
    </div>
    <template #title>
      {{ tooltipText }}
      <slot name="additionalInfo" />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed, isProxy, toRaw } from 'vue';

import BittsAvatar from '../BittsAvatar/BittsAvatar.vue';
import type { BittsAvatarProps } from '../BittsAvatar/BittsAvatar.vue';
import type { TUser } from '../BittsAvatar/types';
import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';

export type BittsAvatarPipSize = 'large' | 'medium' | 'small';
export type BittsAvatarPipUser = TUser & { name?: string | null };

type SharedAvatarProps = Pick<
  BittsAvatarProps,
  | 'altTextFromParent'
  | 'org'
  | 'imageSource'
  | 'includeHoverText'
  | 'showInitials'
  | 'isIcon'
  | 'iconSlotBgColor'
>;

const {
  altTextFromParent = '',
  size = 'medium',
  org = null,
  user = null,
  imageSource = '',
  includeHoverText = false,
  isUserMainAvatar = true,
  showInitials = false,
  isIcon = false,
  iconSlotBgColor = 'white',
  hoverTextPlacement = 'bottom',
  showTooltipOrgInfo = true,
} = defineProps<
  SharedAvatarProps & {
    size?: BittsAvatarPipSize;
    user?: BittsAvatarPipUser | null;
    isUserMainAvatar?: boolean;
    hoverTextPlacement?: string;
    showTooltipOrgInfo?: boolean;
  }
>();

const userFromProp = computed(() =>
  user && isProxy(user) ? toRaw(user) : user,
);
const orgFromProp = computed(() => (org && isProxy(org) ? toRaw(org) : org));

const parentHeight = computed(
  () =>
    ({
      small: 'h-24',
      medium: 'h-32',
      large: 'h-64',
    })[size],
);

const pipContainerSize = computed(
  () =>
    ({
      small: 'leading-2',
      medium: 'leading-4',
      large: 'leading-6',
    })[size],
);

const pipOutlineClass = computed(
  () =>
    ({
      small: 'bitts-avatar-pip__outline-small',
      medium: 'bitts-avatar-pip__outline-medium',
      large: 'bitts-avatar-pip__outline-large',
    })[size],
);

const pipSizeMap: Record<BittsAvatarPipSize, 'tiny' | 'x-small' | 'small'> = {
  small: 'tiny',
  medium: 'x-small',
  large: 'small',
};
const pipSize = computed(() => pipSizeMap[size]);

const tooltipOrDiv = computed(() => (includeHoverText ? BittsTooltip : 'div'));

const tooltipProps = computed(() => {
  if (includeHoverText) {
    return {
      trigger: 'hover',
      placement: hoverTextPlacement,
    };
  }
  return {};
});

const tooltipText = computed(() => {
  let text = '';
  const currentUser = userFromProp.value;
  const currentOrg = orgFromProp.value;
  if (currentUser) {
    if (currentUser.name) {
      text += currentUser.name;
    } else if (currentUser.first_name) {
      text += currentUser.first_name;
      if (currentUser.last_name) {
        text += ` ${currentUser.last_name}`;
      }
    }
    if (currentOrg && currentOrg.name && showTooltipOrgInfo) {
      text += ` at ${currentOrg.name}`;
    }
  }
  return text;
});

function userOrOrg(
  mainAvatar: boolean,
): { user: BittsAvatarPipUser | null } | { org: BittsAvatarProps['org'] } {
  if (mainAvatar) {
    return isUserMainAvatar
      ? { user: userFromProp.value }
      : { org: orgFromProp.value };
  }
  return !isUserMainAvatar
    ? { user: userFromProp.value }
    : { org: orgFromProp.value };
}
</script>

<style lang="pcss">
.bitts-avatar-pip {
  position: relative;
  .bitts-avatar-pip__pip {
    position: absolute;
    bottom: 0;
    right: 0;
  }
  .bitts-avatar-pip__outline-large {
    outline: solid 4px white;
  }
  .bitts-avatar-pip__outline-medium {
    outline: solid 2px white;
  }
  .bitts-avatar-pip__outline-small {
    outline: solid 1px white;
  }
}
</style>
